import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table, Input, Select, Button, Modal, Form, InputNumber,
  Upload, Tag, Tooltip, Popconfirm, message,
} from "antd";
import {
  SearchOutlined, UploadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, ReloadOutlined, SaveOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import api from "../../../core/axios/axiosInstance";

const { Option } = Select;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function CallRates() {
  const profile      = useSelector((s) => s.profile);
  const isSuperAdmin = profile?.role === "superadmin";

  const [rates,         setRates]         = useState([]);
  const [countries,     setCountries]     = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [uploading,     setUploading]     = useState(false);
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(25);
  const [search,        setSearch]        = useState("");
  const [countryFilter, setCountryFilter] = useState("all");

  // Global commission (superadmin only)
  const [commission,    setCommission]    = useState(0);
  const [commInput,     setCommInput]     = useState(0);
  const [savingComm,    setSavingComm]    = useState(false);

  // Add / Edit modal
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [form]                      = Form.useForm();

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchRates = useCallback(async (p = page, ps = pageSize, s = search, c = countryFilter) => {
    setLoading(true);
    try {
      const params = { page: p, limit: ps };
      if (s)           params.search  = s;
      if (c !== "all") params.country = c;
      const res = await api.get("/call-rates", { params });
      setRates(res.data.data || []);
      setTotal(res.data.total || 0);
      setCountries(res.data.countries || []);
      // Global commission is returned only for superadmin
      if (res.data.commission !== undefined) {
        setCommission(res.data.commission);
        setCommInput(res.data.commission);
      }
    } catch {
      message.error("Failed to load call rates.");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRates(1, 25, "", "all"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced search ─────────────────────────────────────────────────────
  const searchDebounceRef = useRef(null);
  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchRates(1, pageSize, val, countryFilter);
    }, 400);
  };

  const handleCountry = (val) => {
    setCountryFilter(val);
    setPage(1);
    fetchRates(1, pageSize, search, val);
  };

  const handlePageChange = (p, ps) => {
    setPage(p);
    setPageSize(ps);
    fetchRates(p, ps, search, countryFilter);
  };

  const handleSaveCommission = async () => {
    setSavingComm(true);
    try {
      await api.put("/call-rates/config", { commission: Number(commInput) });
      setCommission(Number(commInput));
      message.success("Commission updated.");
      // Refresh table so customer rates recalculate
      fetchRates(page, pageSize, search, countryFilter);
    } catch {
      message.error("Failed to save commission.");
    } finally {
      setSavingComm(false);
    }
  };

  const openAdd = () => {
    setEditRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({ country: record.country, prefix: record.prefix, standardRate: record.standardRate });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      if (editRecord) {
        await api.put(`/call-rates/${editRecord._id}`, vals);
        message.success("Rate updated.");
      } else {
        await api.post("/call-rates", vals);
        message.success("Rate added.");
      }
      setModalOpen(false);
      fetchRates(page, pageSize, search, countryFilter);
    } catch (err) {
      if (err?.response) message.error(err.response.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/call-rates/${id}`);
      message.success("Rate deleted.");
      fetchRates(page, pageSize, search, countryFilter);
    } catch {
      message.error("Failed to delete.");
    }
  };

  const handleUpload = async ({ file }) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/call-rates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      message.success(d.message || `Imported ${d.imported} rates.`);
      if (d.errors?.length) {
        Modal.warning({
          title:   `${d.skipped} row(s) skipped`,
          content: (
            <ul style={{ maxHeight: 200, overflowY: "auto", paddingLeft: 18, margin: 0 }}>
              {d.errors.map((e, i) => <li key={i} style={{ fontSize: 12 }}>{e}</li>)}
            </ul>
          ),
        });
      }
      setPage(1); setSearch(""); setCountryFilter("all");
      fetchRates(1, pageSize, "", "all");
    } catch (err) {
      message.error(err.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDownloadSample = () => {
    const XLSX = require("xlsx");
    const rows = [
      ["Name", "Prefix", "Standard Rate"],
      ["United Arab Emirates", "+971", 0.0250],
      ["United States", "+1", 0.0100],
      ["United Kingdom", "+44", 0.0150],
      ["Saudi Arabia", "+966", 0.0300],
      ["India", "+91", 0.0080],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Call Rates");
    XLSX.writeFile(wb, "call_rates_sample.xlsx");
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title:     "Country",
      dataIndex: "country",
      key:       "country",
      render:    (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title:     "Prefix",
      dataIndex: "prefix",
      key:       "prefix",
      render:    (v) => <Tag color="blue" style={{ fontFamily: "monospace" }}>{v}</Tag>,
    },
    // Superadmin sees Standard Rate + Customer Rate; company admin sees only Rate
    ...(isSuperAdmin
      ? [
          {
            title:     "Standard Rate ($/min)",
            dataIndex: "standardRate",
            key:       "standardRate",
            align:     "right",
            render:    (v) => <span style={{ color: "#595959", fontWeight: 600 }}>${Number(v).toFixed(4)}</span>,
          },
          {
            title:     "Customer Rate ($/min)",
            dataIndex: "customerRate",
            key:       "customerRate",
            align:     "right",
            render:    (v) => <span style={{ fontWeight: 700, color: "#3C2371" }}>${Number(v || 0).toFixed(4)}</span>,
          },
        ]
      : [
          {
            title:     "Rate ($/min)",
            dataIndex: "customerRate",
            key:       "customerRate",
            align:     "right",
            render:    (v) => <span style={{ fontWeight: 700, color: "#3C2371" }}>${Number(v || 0).toFixed(4)}</span>,
          },
        ]),
    ...(isSuperAdmin
      ? [{
          title:  "Actions",
          key:    "actions",
          align:  "center",
          width:  90,
          render: (_, r) => (
            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
              <Tooltip title="Edit">
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
              </Tooltip>
              <Popconfirm title="Delete this rate?" onConfirm={() => handleDelete(r._id)} okText="Delete" okButtonProps={{ danger: true }}>
                <Tooltip title="Delete">
                  <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
                </Tooltip>
              </Popconfirm>
            </div>
          ),
        }]
      : []),
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <div className="content">

        {/* Global Commission Card — superadmin only */}
        {isSuperAdmin && (
          <div className="card" style={{ padding: "16px 24px", borderRadius: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e", marginBottom: 2 }}>
                  <i className="ti ti-percentage" style={{ marginRight: 6, color: "#fa8c16" }} />
                  Global Commission
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  Added to every standard rate — customer sees: Standard Rate + Commission
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                <InputNumber
                  min={0}
                  max={1000}
                  step={0.1}
                  precision={2}
                  value={commInput}
                  onChange={(v) => setCommInput(v ?? 0)}
                  style={{ width: 140, borderRadius: 8 }}
                  addonAfter="%"
                />
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingComm}
                  onClick={handleSaveCommission}
                  disabled={Number(commInput) === Number(commission)}
                  style={{ borderRadius: 8, background: "linear-gradient(135deg,#fa8c16,#ffa940)", border: "none" }}
                >
                  Save
                </Button>
                {commission > 0 && (
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    Current: <strong style={{ color: "#fa8c16" }}>{Number(commission).toFixed(2)}%</strong>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Table Card */}
        <div className="card" style={{ padding: "20px 24px", borderRadius: 12 }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h4 style={{ fontWeight: 700, marginBottom: 4 }}>
                <i className="ti ti-phone-call" style={{ marginRight: 8, color: "#667eea" }} />
                Call Rates
              </h4>
              <p style={{ color: "#8c8c8c", margin: 0, fontSize: 13 }}>
                {total} rate{total !== 1 ? "s" : ""} available
              </p>
            </div>

            {isSuperAdmin && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadSample} style={{ borderRadius: 8 }}>
                  Sample XLSX
                </Button>
                <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={(file) => { handleUpload({ file }); return false; }}>
                  <Button icon={<UploadOutlined />} loading={uploading} style={{ borderRadius: 8 }}>Upload XLSX</Button>
                </Upload>
                <Button
                  type="primary" icon={<PlusOutlined />} onClick={openAdd}
                  style={{ borderRadius: 8, background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none" }}
                >
                  Add Rate
                </Button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <Input
              placeholder="Search country or prefix…"
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 260, borderRadius: 8 }}
              onChange={(e) => { if (!e.target.value) handleSearch(""); }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onBlur={(e) => handleSearch(e.target.value)}
            />
            <Select value={countryFilter} onChange={handleCountry} style={{ width: 220 }} showSearch optionFilterProp="children">
              <Option value="all">All Countries</Option>
              {countries.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => fetchRates(page, pageSize, search, countryFilter)} style={{ borderRadius: 8 }} />
            </Tooltip>
          </div>

          {/* Empty state */}
          {isSuperAdmin && rates.length === 0 && !loading && (
            <div style={{ textAlign: "center", padding: "40px 20px", background: "#f8f9ff", borderRadius: 12, border: "1px dashed #c7d2fe", marginBottom: 16 }}>
              <i className="ti ti-file-spreadsheet" style={{ fontSize: 48, color: "#8e77fa", display: "block", marginBottom: 12 }} />
              <p style={{ fontWeight: 600, color: "#3C2371", marginBottom: 6 }}>No call rates yet</p>
              <p style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 16 }}>
                Upload an <strong>XLSX file</strong> with columns: <strong>Name</strong>, <strong>Prefix</strong>, <strong>Standard Rate</strong>
              </p>
              <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={(file) => { handleUpload({ file }); return false; }}>
                <Button type="primary" icon={<UploadOutlined />} loading={uploading}
                  style={{ borderRadius: 8, background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none" }}>
                  Upload XLSX
                </Button>
              </Upload>
            </div>
          )}

          {/* Table */}
          <Table
            dataSource={rates}
            columns={columns}
            rowKey="_id"
            loading={loading}
            pagination={{
              current:          page,
              total,
              pageSize,
              pageSizeOptions:  PAGE_SIZE_OPTIONS,
              showSizeChanger:  true,
              onChange:         handlePageChange,
              onShowSizeChange: handlePageChange,
              showTotal:        (t, [from, to]) => `${from}–${to} of ${t} rates`,
            }}
            size="small"
            scroll={{ x: 600 }}
          />
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        title={editRecord ? "Edit Call Rate" : "Add Call Rate"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editRecord ? "Update" : "Add"}
        okButtonProps={{ loading: saving, style: { background: "linear-gradient(135deg,#3C2371,#8e77fa)", border: "none", borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="country" label="Country" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. United Arab Emirates" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="prefix" label="Prefix" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. +971" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="standardRate" label="Standard Rate ($/min)" rules={[{ required: true, message: "Required" }]}>
            <InputNumber min={0} step={0.0001} precision={4} style={{ width: "100%", borderRadius: 8 }} placeholder="e.g. 0.0250" />
          </Form.Item>
          {/* Live preview */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
            {() => {
              const sr = form.getFieldValue("standardRate") || 0;
              const cr = Number((sr * (1 + commission / 100)).toFixed(4));
              return (
                <div style={{ background: "#f8f9ff", border: "1px solid #e8edff", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                  <span style={{ color: "#6b7280" }}>Customer sees: </span>
                  <strong style={{ color: "#3C2371" }}>${cr.toFixed(4)}/min</strong>
                  <span style={{ color: "#8c8c8c", fontSize: 11, marginLeft: 8 }}>
                    (${Number(sr).toFixed(4)} + {Number(commission).toFixed(2)}% commission)
                  </span>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
